import { RefObject, useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keyboard behaviour for a modal dialog: moves focus into the dialog when it opens, keeps
 * Tab and Shift+Tab inside it, closes it on Escape, and returns focus to whatever had it
 * before the dialog opened. Elements inside an `inert` subtree are skipped.
 */
export const useDialogFocus = (
  containerRef: RefObject<HTMLElement>,
  onEscape: () => void,
  active = true
) => {
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active) return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusables = (): HTMLElement[] =>
      Array.from(containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(
        (element) => !element.closest('[inert]')
      );

    const container = containerRef.current;
    if (container && !container.contains(document.activeElement)) {
      // A dialog that can take focus takes it itself: focusing its first button on open drew that
      // button's focus ring before the player had touched the keyboard (the entry modal's close
      // button). The first Tab still lands on the first control.
      const target = container.hasAttribute('tabindex') ? container : focusables()[0] ?? container;
      target.focus({ preventScroll: true });
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onEscapeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      // Focus on the dialog itself or anywhere outside it (e.g. a click on the page behind)
      // re-enters at the edge Tab is heading for, instead of moving on through the page.
      if (current === containerRef.current || !containerRef.current?.contains(current)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && current === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [active, containerRef]);
};
